<script setup>
import { useCouponsStore } from '@/stores/coupons';
import { onBeforeMount, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css';
import { useI18n } from 'vue-i18n';
import { useRegionsStore } from '@/stores/regions';
import { usePreSportsStore } from '@/stores/pre_sports';
import { useDateTimeSportFiltersStore } from '@/stores/date_time_sport_filters';

const { t } = useI18n();
const router = useRouter();
const couponsStore = useCouponsStore();
const regionsStore = useRegionsStore();
const preSportsStore = usePreSportsStore();
const dateTimeSportFiltersStore = useDateTimeSportFiltersStore();

const selected_option = ref('daily');
const direction = ref(document.dir);

const splideOptions = ref({
	direction: direction.value,
	pagination: false,
	arrows: true,
	autoWidth: true,
	trimSpace: 'move',
	gap: '1rem',
	breakpoints: {
		640: {
			gap: '0.5rem',
		},
	},
});

const selectDay = (day) => {
	dateTimeSportFiltersStore.selected_option = day.slug;

	dateTimeSportFiltersStore.date_from = day.slug;

	if (day.slug === 'all') {
		regionsStore.fetchRegions(preSportsStore.selected_pre_sport_id).then(() => {
			couponsStore.pruneSelectedCompetitions(regionsStore.regions_competitions);
		});
	} else {
		dateTimeSportFiltersStore.fetchCompetitionsPerRegion().then(() => {
			couponsStore.pruneSelectedCompetitions(regionsStore.regions_competitions);
		});
	}
}

const selectHour = (hour) => {
	dateTimeSportFiltersStore.selected_option = hour.slug;
	dateTimeSportFiltersStore.date_from = hour.slug;

	dateTimeSportFiltersStore.fetchCompetitionsPerRegion().then(() => {
		couponsStore.pruneSelectedCompetitions(regionsStore.regions_competitions);
	});
}

const expand = () => {
	couponsStore.toggleAllExpanded();
}

onMounted(() => {
	if (dateTimeSportFiltersStore.selected_option === 'all') {
		regionsStore.fetchRegions(preSportsStore.selected_pre_sport_id).then(() => {
			couponsStore.pruneSelectedCompetitions(regionsStore.regions_competitions);
		});
	} else {
		dateTimeSportFiltersStore.fetchCompetitionsPerRegion().then(() => {
			couponsStore.pruneSelectedCompetitions(regionsStore.regions_competitions);
		});
	}
});
onBeforeMount(() => {
	let hours = ['1_hours', '3_hours', '6_hours', '12_hours', '24_hours', '48_hours'];

	if (hours.includes(dateTimeSportFiltersStore.selected_option)) selected_option.value = 'hours';
	preSportsStore.setSelectedPreSportId(router.currentRoute.value.params.sport_id);

	dateTimeSportFiltersStore.date_from = dateTimeSportFiltersStore.selected_option;

})
</script>
<template>
	<div class="coupon-section-header">
		<div class="tab">
			<button class="tablinks" :class="{'active': selected_option == 'daily'}" @click="selected_option = 'daily'">{{ t('general.daily') }}</button>
			<button class="tablinks" :class="{'active': selected_option == 'hours'}" @click="selected_option = 'hours'">{{ t('general.hours') }}</button>

			<span class="toggle-markets" v-if="router.currentRoute.value.name == 'pre-sports-coupons'">
				<div @click="expand" class="markets-toggle-fill">
					<svg v-if="couponsStore.all_expanded" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" svg-inline="" title="Toggle" role="presentation" focusable="false" xmlns:xlink="http://www.w3.org/1999/xlink" class="markets-toggle-svg">
						<use xlink:href="/assets/icons/markets_close.svg#markets_close"></use>
					</svg>
					<svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" svg-inline="" title="Toggle" role="presentation" focusable="false" xmlns:xlink="http://www.w3.org/1999/xlink" class="markets-toggle-svg">
						<use xlink:href="/assets/icons/markets_open.svg#markets_open"></use>
					</svg>
				</div>
			</span>
		</div>
	</div>

	<div class="filters-wrapper">
		<Transition name="slide-fade">
			<Splide v-if="selected_option == 'daily'" :options="splideOptions" class="splide-container">
				<SplideSlide v-for="day in dateTimeSportFiltersStore.upcoming_days_info" :key="day.slug">
					<div class="coupon-checkbox-outer" :class="{'selected': dateTimeSportFiltersStore.selected_option == day.slug}" @click="selectDay(day)">
						<span>{{ $t('days.' + day.slug) }}</span>
					</div>
				</SplideSlide>
			</Splide>
		</Transition>

		<Transition name="slide-fade">
			<Splide v-if="selected_option == 'hours'" :options="splideOptions" class="splide-container">
				<SplideSlide v-for="hour in dateTimeSportFiltersStore.upcoming_hours_info" :key="hour.slug">
					<div class="coupon-checkbox-outer" :class="{'selected': dateTimeSportFiltersStore.selected_option == hour.slug}" @click="selectHour(hour)">
						<span>{{ $t('day_time_selector.next_' + hour.slug) }}</span>
					</div>
				</SplideSlide>
			</Splide>
		</Transition>
	</div>

</template>
<style scoped>
	.coupon-section-header {
		margin-bottom: 8px !important;
	}

	html[dir="rtl"] .coupons-section-outer .coupon-section-header .show-coupons-btn {
		text-align: left;
		position: relative;
	}

	.tab .active:after {
		bottom: -0.5px;
	}

	.coupon-checkbox-outer {
		padding: 5px 10px;
		display: flex;
		align-items: center;
		background: transparent;
		border-radius: 5px;
		transition: 0.3s ease-in-out;
		color: var(--white);
		text-wrap: nowrap;
		border: 1px solid var(--tab-gray);
		cursor: pointer;
		gap: 5px;
	}

	@media(pointer:fine){
		.coupon-checkbox-outer:hover {
			background-color: var(--primary);
			border-color: var(--primary);
		}
	}

	.coupon-checkbox-outer span {
		font-size: 12px;
		font-weight: 500;
	}

	.coupon-checkbox-outer.selected {
		background-color: var(--primary);
		border-color: var(--primary);
	}

	:deep(.splide__arrow:disabled) {
		display: none !important;
	}

	@media screen and (min-width: 320px) and (max-width: 767px) {
		:deep(.splide__arrows .splide__arrow--next) {
			right: 0px !important;
		}
	}

	@media screen and (min-width: 320px) and (max-width: 767px) {
		:deep(.splide__arrows .splide__arrow--prev) {
			left: 0px !important;
		}
	}

	html[dir="rtl"] .splide-container :deep(.splide__arrows .splide__arrow--next) {
	rotate: 180deg;
	left: -10px;
	top: -11px;
	display: flex;
	right: unset;
}

html[dir="rtl"] .splide-container :deep(.splide__arrows .splide__arrow--prev) {
	rotate: 180deg;
	top: -11px;
	right: 0px;
}


@media screen and (min-width: 320px) and (max-width: 767px) {

	html[dir="rtl"] .splide-container :deep(.splide__arrows .splide__arrow--next) {
		left: 0px !important;
		right: unset !important;

	}

	html[dir="rtl"].splide-container :deep(.splide__arrows .splide__arrow--prev) {
		rotate: 180deg !important;
		right: 0px !important;
		left: unset !important;
	}

}

@media screen and (min-width: 768px) and (max-width: 1280px) {
	html[dir="rtl"] .splide-container :deep(.splide__arrows .splide__arrow--next) {
		left: 0px;
		right: unset !important;
	}

html[dir="rtl"].splide-container :deep(.splide__arrows .splide__arrow--prev) {
		rotate: 180deg !important;
		right: 0px !important;
		left: unset !important;
	}

}

html[dir="rtl"] .toggle-markets {


    display: flex;
    flex-direction: row;
    justify-content: flex-end;

}

	.slide-fade-enter-active {
		transition: all 0.3s ease-out;
	}

	.slide-fade-leave-active {
		display: none;
	}

	.slide-fade-enter-from {
		transform: translateY(-10px);
		opacity: 0;
	}

	.tablinks {
		flex: 1;
	}

	.toggle-markets {
		flex: 1;
		text-align: -webkit-right;
	}

	.splide-container {
		margin-bottom: 22px;
	}

	.filters-wrapper {
		min-height: 40px;
	}
</style>